import { ModalAction } from '@overlayqa/shared/ui';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useDeleteProject } from '../../../hooks/useProjects';
import { useBulkArchiveProjects } from '../../../hooks/useBulkActions';
import { DeletionConfirmDialog } from '@/components/shared/DeletionConfirmDialog';
import { log } from '@/lib/logger';

interface DangerZoneTabProps {
  project: { id: string };
  onClose: () => void;
}

/**
 * The owner-only Danger Zone tab (Emily, 2026-09-17: "move danger zone to a new
 * tab"). Moved from the retired settings page (IssuesPageSettings.tsx); both
 * actions still wait for the server before leaving, and now close the modal
 * first so it does not linger over the Projects page.
 */
export function DangerZoneTab({ project, onClose }: DangerZoneTabProps) {
  const navigate = useNavigate();
  const deleteProject = useDeleteProject();
  const bulkArchive = useBulkArchiveProjects();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);

  const handleArchive = async () => {
    setIsArchiving(true);
    try {
      await bulkArchive.mutateAsync({ ids: [project.id] });
      // Leave ONLY on success: the old fire-and-forget navigated away even
      // when the archive was refused.
      onClose();
      navigate('/projects');
    } catch (err) {
      // useBulkArchiveProjects already toasts via its hook-level onError; log
      // here so the rejection isn't unhandled.
      log.error('[DangerZoneTab] Failed to archive project', { projectId: project.id, error: err instanceof Error ? err.message : String(err) });
    } finally {
      setIsArchiving(false);
    }
  };

  return (
    <div className="px-6 pt-4 pb-5">
      <section className="card-sm border-red-200" aria-labelledby="danger-zone-heading">
        <h3 id="danger-zone-heading" className="section-label text-red-600 mb-3">Danger Zone</h3>
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            className="btn-secondary whitespace-nowrap text-sm"
            disabled={isArchiving || bulkArchive.isPending}
            onClick={() => void handleArchive()}
          ><ModalAction kind="archive">
            {isArchiving ? 'Archiving...' : 'Archive project'}
          </ModalAction></button>
          <button
            type="button"
            /* Outline-red is the ENTRY to the destructive flow; the confirm
               dialog's own button is solid red (ConfirmDialog.tsx), so the two
               read as a ladder rather than two identical commits. Utilities
               override .btn-secondary's gray because Tailwind's utilities layer
               beats the components layer an @apply'd class sits in, the same
               technique ConfirmDialog uses for its red confirm. Hand-rolled, it
               rendered 34px at an 8px radius beside a 36px/6px Archive Project
               and carried no focus ring, no press feedback and no disabled
               treatment. */
            className="btn-secondary whitespace-nowrap border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
            onClick={() => setConfirmDelete(true)}
          ><ModalAction kind="delete">
            Delete project
          </ModalAction></button>
        </div>
      </section>

      <DeletionConfirmDialog
        open={confirmDelete}
        request={confirmDelete ? { kind: 'projects', ids: [project.id] } : null}
        fallback={{ title: 'Delete project', message: 'Delete this project and all its issues? This cannot be undone.', confirmLabel: 'Delete' }}
        onConfirm={async () => {
          log.info('[DangerZoneTab] Delete project confirmed', { projectId: project.id });
          try {
            await deleteProject.mutateAsync(project.id);
            onClose();
            navigate('/projects', { replace: true });
            toast.success('Project deleted');
          } catch (err) {
            log.error('[DangerZoneTab] Delete project failed', { projectId: project.id, error: err instanceof Error ? err.message : String(err) });
            toast.error('Failed to delete project');
            setConfirmDelete(false);
          }
        }}
        onCancel={() => setConfirmDelete(false)}
        loading={deleteProject.isPending}
      />
    </div>
  );
}
